import React, { useState } from 'react';
import { useDrag, DragPreviewImage } from 'react-dnd';
import { Element as ElementType } from '../types';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { FaStar } from 'react-icons/fa';
import { useInteractionMode } from '../contexts/InteractionModeContext';
import { useReportingMode } from '../contexts/ReportingModeContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

interface ElementProps {
  element: ElementType;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  onDragStateChange?: (isDragging: boolean) => void;
  className?: string;
  isHighlighted?: boolean;
  isNew?: boolean; // Flag to indicate if this is a newly discovered element
  onAddToFavorites?: () => void;
  style?: React.CSSProperties;
  context?: 'panel' | 'machine'; // 'panel' for Elements & Discoveries, 'machine' for inputs/outputs
}

const Element: React.FC<ElementProps> = ({
  element,
  onDragStart,
  onDragEnd,
  onDragStateChange,
  className = '',
  isHighlighted = false,
  isNew = false,
  onAddToFavorites,
  style = {},
  context = 'machine', // Default to machine context
}) => {
  // Get interaction mode from context
  const { mode, selectedElement, setSelectedElement } = useInteractionMode();

  // Get reporting mode from context
  const { isReportingMode, reportElementHandler } = useReportingMode();

  // Check if this element is the currently selected one
  const isSelected = selectedElement?.id === element.id;

  // Use the element's color if available, otherwise fall back to the default color based on type
  const elementColor = element.color || getDefaultColorByType(element.type);

  // Handle tap/click for tap-to-location mode, reporting mode, and delete element mode
  const handleTap = () => {
    // If in reporting mode, report this element
    if (isReportingMode) {
      reportElementHandler(element);
      return;
    }

    // If in delete element mode, select this element for deletion
    if (
      document.body.classList.contains('delete-element-mode') &&
      typeof window.selectElementForDeletion === 'function'
    ) {
      window.selectElementForDeletion(element);
      return;
    }

    // Normal tap-to-location behavior
    if (mode === 'tap-to-location') {
      // If this element is already selected, deselect it
      if (isSelected) {
        setSelectedElement(null);
      } else {
        // Otherwise, select this element
        setSelectedElement(element);
      }
    }
  };

  // Create a drag source - simplified approach
  const [{ isDragging }, dragRef, preview] = useDrag(
    () => ({
      type: 'element',
      item: element,
      end: (item, monitor) => {
        if (onDragEnd) onDragEnd();
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [element, onDragEnd]
  );

  // Use an empty image as the drag preview (we'll create our own custom preview)
  React.useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  // Call onDragStateChange when isDragging changes
  React.useEffect(() => {
    if (onDragStateChange) {
      onDragStateChange(isDragging);
    }
  }, [isDragging, onDragStateChange]);

  // No click-to-click functionality

  // Different styling based on context
  const getContextStyles = () => {
    if (context === 'panel') {
      // For Elements & Discoveries panel
      return {
        wrapper: 'p-[1px]',
        elementStyles: {
          backgroundColor: elementColor,
          color: getContrastTextColor(elementColor),
          whiteSpace: 'nowrap' as const,
          width: 'auto',
          display: 'inline-flex',
          borderRadius: '0.75rem',
          ...style,
        },
      };
    } else {
      // For machine inputs/outputs
      return {
        wrapper: 'p-[1px]',
        elementStyles: {
          backgroundColor: elementColor,
          color: getContrastTextColor(elementColor),
          whiteSpace: 'normal' as const,
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: '0.75rem',
          ...style,
        },
      };
    }
  };

  const contextStyles = getContextStyles();

  // Simplified render function
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            ref={dragRef}
            onClick={handleTap}
            className={`
              element
              flex items-center justify-center
              px-3 py-1.5 ${
                isReportingMode || document.body.classList.contains('delete-element-mode')
                  ? 'cursor-pointer'
                  : mode === 'drag-and-drop'
                  ? 'cursor-grab'
                  : 'cursor-pointer'
              }
              text-center
              leading-relaxed
              rounded-xl
              ${className}
              ${isHighlighted ? 'ring-1 ring-primary' : ''}
              ${isSelected ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''}
              ${isDragging ? 'opacity-50' : ''}
              ${
                isReportingMode
                  ? 'hover:ring-2 hover:ring-destructive hover:ring-offset-2 hover:ring-offset-background'
                  : document.body.classList.contains('delete-element-mode')
                  ? 'hover:ring-2 hover:ring-destructive hover:ring-offset-2 hover:ring-offset-background'
                  : ''
              }
            `}
            style={{
              backgroundColor: elementColor,
              color: getContrastTextColor(elementColor),
              ...style,
            }}
          >
            <span className="element-text select-none leading-relaxed" style={{ lineHeight: 1.4 }}>
              {element.name}
            </span>

            {/* Gold star for new elements */}
            {isNew && context === 'machine' && (
              <div className="absolute -top-2 -right-2 animate-pulse">
                <FaStar className="text-yellow-400 text-xl" />
              </div>
            )}
          </div>
        </TooltipTrigger>
        {/* Only show tooltip if element has a description */}
        {element.description && (
          <TooltipContent side="top" className="max-w-xs">
            <p>{element.description}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};

// Helper function to get default color by element type
function getDefaultColorByType(type: ElementType['type']): string {
  switch (type) {
    case 'water':
      return '#3b82f6';
    case 'fire':
      return '#ef4444';
    case 'dirt':
      return '#8B6F4E';
    case 'air':
      return '#e2e8f0';
    case 'discovery':
      return '#8b5cf6';
    default:
      return '#8b5cf6';
  }
}

// Helper function to determine if text should be white or black based on background color
function getContrastTextColor(hexColor: string): string {
  hexColor = hexColor.replace('#', '');

  const r = parseInt(hexColor.substring(0, 2), 16);
  const g = parseInt(hexColor.substring(2, 4), 16);
  const b = parseInt(hexColor.substring(4, 6), 16);

  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  return brightness > 128 ? '#000000' : '#ffffff';
}

export default Element;
